import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getArticlesByMediaId } from "../services/medias.service";
import { syncCombinationArticles } from "../services/articles.service";
import { QueryKeys } from "./queryKeys";
import { useMemo } from "react";
import { SyncArticle, Article } from "../types/article";
import { enrichToAddArticles } from "../utils/localCombinationDiff";

export const useArticlesByMediaId = (
  mediaId?: string | undefined,
  active?: "ACTIVE" | "INACTIVE" | undefined
) => {
  const {
    data: articles,
    isLoading,
    isError,
  } = useQuery({
    queryKey: [QueryKeys.ArticlesByMedia, { id: mediaId }],
    queryFn: () => getArticlesByMediaId(mediaId),
    enabled: !!mediaId,
  });
  const data = useMemo(
    () => articles?.filter((a) => !active || a.active === active) ?? articles,
    [articles, active]
  );
  return { data, isLoading, isError };
};

export function useSyncCombinationArticles(
  combinationArticlesCollection: Article[]
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      toAdd,
      toUpdate,
      toDelete,
    }: {
      toAdd: SyncArticle[];
      toUpdate: Article[];
      toDelete: { id: string }[];
    }) => {
      const enrichedToAdd = await enrichToAddArticles(
        toAdd,
        combinationArticlesCollection
      );

      return syncCombinationArticles({
        toAdd: enrichedToAdd,
        toUpdate,
        toDelete,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["articlesByMedia"] });
    },
  });
}
